import { memo } from "react";

import { NetworkStatus } from "@apollo/client";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

import InfiniteScrollWrapper from "../../../../common/components/infinite-scroll/InfiniteScrollWrapper";
import FullPageLoader from "../../../../common/components/item/FullPageLoader";
import TableNoDataOverlay from "../../../../common/components/table/TableNoDataOverlay";
import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";
import { useSelectedLocation } from "../../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../../common/variables/selectedModel";
import { useGetRecentEvents } from "../../hooks/events/useGetRecentEvents";
import BasicEventTable from "./BasicEventTable";
import GasLeakEventTable from "./GasLeakEventTable";

const EventTableContainer = (): JSX.Element => {
  const customerId = useCustomerIdGuard();
  const modelId = useSelectedModel();
  const facilityId = useSelectedLocation();

  const {
    events,
    loading,
    fetchNextPage,
    lastEvaluatedKey,
    fetchMoreLoading,
    networkStatus,
  } = useGetRecentEvents({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: facilityId?.value ?? "",
  });

  if (loading) return <FullPageLoader />;

  if (!events.length && !loading) {
    return <TableNoDataOverlay />;
  }

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <InfiniteScrollWrapper
        fetchNextPage={fetchNextPage}
        lastEvaluatedKey={lastEvaluatedKey}
        loading={loading || fetchMoreLoading}
      >
        {events.map(({ event, newestRecording }) => {
          switch (event.modelId) {
            case "GASLEAK":
              return (
                <GasLeakEventTable
                  event={event}
                  newestRecording={newestRecording}
                  key={event.id}
                />
              );
            default:
              return (
                <BasicEventTable
                  event={event}
                  newestRecording={newestRecording}
                  key={event.id}
                />
              );
          }
        })}
      </InfiniteScrollWrapper>
      {fetchMoreLoading && networkStatus !== NetworkStatus.error && (
        <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
      )}
    </Box>
  );
};

export default memo(EventTableContainer);
